import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Chip
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { AllDayCheckDto } from '../../types';
import { getOrderStatusColor, getCloudStatusColor } from '../../utils/helpers';
import { OrderStatus, CloudOrderState } from '../../types';

interface AllDayChecksReportProps {
  checks: AllDayCheckDto[];
  startDate: Date | null;
  endDate: Date | null;
  selectedBranchName: string;
}

const AllDayChecksReport: React.FC<AllDayChecksReportProps> = ({
  checks,
  startDate,
  endDate,
  selectedBranchName,
}) => {
  const theme = useTheme();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EGP' }).format(amount);
  };

  const formatDateShort = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };
  
  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, HH:mm');
  };

  const handlePrint = () => {
    window.print();
  };

  if (checks.length === 0) {
    return null;
  }

  return (
    <div className="all-day-checks-report-container">
      <style>
        {`
          /* Container styling */
          .all-day-checks-report-container {
            background: ${theme.palette.background.paper};
            padding: 0;
            margin: 0;
          }
          
          .print-header {
            border-bottom: 2px solid ${theme.palette.text.primary};
            margin-bottom: 8px;
            padding: 2px 15px 5px 15px;
            page-break-inside: avoid;
          }
          
          .print-header h1 {
            font-size: 24px;
            font-weight: bold;
            color: ${theme.palette.text.primary};
            margin: 0;
            text-align: center;
          }
          
          .app-name {
            font-size: 16px;
            font-weight: 600;
            color: ${theme.palette.primary.main};
            margin-bottom: 5px;
            letter-spacing: 1px;
            text-align: left;
          }
          
          .report-filters {
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          
          .report-filters > div {
            display: flex !important;
            align-items: center;
            gap: 20px;
            padding: 5px 15px;
            margin: 0 15px 10px 15px;
            font-size: 14px;
          }
          
          @media print {
            @page {
              margin: 10mm 12mm;
              size: A4 landscape;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              box-sizing: border-box !important;
            }
            
            html, body {
              background: white !important;
              color: black !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              position: relative !important;
            }
            
            body > *, #root, #root > *, main {
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              position: relative !important;
            }
            
            .print-header {
              margin-bottom: 5mm !important;
              border-bottom: 2px solid black !important;
              padding: 2mm !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            .print-header h1 {
              font-size: 18px !important;
              margin: 0 0 3mm 0 !important;
              text-align: center !important;
              font-weight: bold !important;
              color: black !important;
            }
            
            .app-name {
              font-size: 12px !important;
              color: black !important;
            }
            
            .report-filters {
              display: block !important;
              margin-bottom: 3mm !important;
              page-break-inside: avoid;
            }
            
            .report-filters > div {
              display: flex !important;
              border-bottom: 1px solid #ccc !important;
              color: black !important;
              font-size: 11px !important;
              padding: 2mm 3mm !important;
              margin: 0 3mm 3mm 3mm !important;
            }
            
            .all-day-checks-report-container {
              background: white !important;
              color: black !important;
              display: block !important;
            }
            
            table, .MuiTable-root {
              border-collapse: collapse !important;
              color: black !important;
              background: white !important;
              width: 100% !important;
            }
            
            th, td, .MuiTableCell-root {
              color: black !important;
              background: white !important;
              border: 1px solid #ccc !important;
              padding: 4px 8px !important;
              font-size: 10px !important;
            }
            
            thead, .MuiTableHead-root {
              display: table-header-group !important;
            }
            
            th, .MuiTableCell-head {
              background-color: #f5f5f5 !important;
              font-weight: bold !important;
            }
            
            tbody, .MuiTableBody-root {
              display: table-row-group !important;
            }
            
            tr {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }

            .MuiChip-root {
                border: 1px solid #000 !important;
                background: none !important;
                color: #000 !important;
                height: 20px !important;
            }
            .MuiChip-label {
                padding: 0 4px !important;
                font-size: 9px !important;
            }
          }
        `}
      </style>
      
      {/* Header Section */}
      <div className="print-header">
        <div style={{ marginBottom: '10px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div className="app-name" style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.primary.main,
              letterSpacing: '1px'
            }}>
              Dining-X
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: theme.palette.text.primary,
              letterSpacing: '1px'
            }}>
              {checks.length > 0 && checks[0].restaurantName ? checks[0].restaurantName : 'Restaurant Name'}
            </div>
          </div>
          <h1 style={{ fontWeight: 'bold', color: theme.palette.text.primary, margin: 0, textAlign: 'center' }}>
            All Day Checks Report
          </h1>
        </div>
      </div>

      {/* Filter Information */}
      <div className="report-filters">
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '5px 15px',
          margin: '0 15px 10px 15px',
          fontSize: '14px',
          minHeight: 'auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary
        }}>
          <span><strong>Period:</strong> {startDate ? formatDateShort(startDate.toISOString()) : '-'} - {endDate ? formatDateShort(endDate.toISOString()) : '-'}</span>
          <span><strong>Branch:</strong> {selectedBranchName || 'All Branches'}</span>
          <span><strong>Total Checks:</strong> {checks.length}</span>
        </div>
      </div>
      
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          variant="outlined"
        >
          Print
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cloud #</TableCell>
              <TableCell>POS #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Cloud Status</TableCell>
              <TableCell>POS Status</TableCell>
              <TableCell align="right">Total Items</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell>Payment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.map((check) => (
              <TableRow key={check.orderId}>
                <TableCell>{check.shortCode}</TableCell>
                <TableCell>{check.localOrderNumber}</TableCell>
                <TableCell>{formatDateTime(check.orderDate)}</TableCell>
                <TableCell>{check.branchName}</TableCell>
                <TableCell>
                    <Chip 
                        label={check.cloudStatus} 
                        size="small" 
                        color={getCloudStatusColor(check.cloudStatus as unknown as CloudOrderState)} 
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Chip 
                        label={check.status} 
                        size="small" 
                        color={getOrderStatusColor(check.status as unknown as OrderStatus)} 
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="right">{check.totalItems}</TableCell>
                <TableCell align="right">{formatCurrency(check.totalAmount)}</TableCell>
                <TableCell>{check.paymentMethod}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AllDayChecksReport;
